export type FranceTravailFormation = {
  codeFormation: string;
  domaineLibelle: string;
  niveauLibelle: string;
  commentaire: string;
  exigence: string;
};

export type FranceTravailLanguage = {
  libelle: string;
  exigence: string;
};

export type FranceTravailPermit = {
  libelle: string;
  exigence: string;
};

export type FranceTravailCompetence = {
  code: string;
  libelle: string;
  exigence: string;
};

export type FranceTravailProfessionalQuality = {
  libelle: string;
  description: string;
};

export type FranceTravailContractType = {
  code: string;
  libelle: string;
};

export type FranceTravailCountry = {
  code: string;
  libelle: string;
};

export type FranceTravailPartner = {
  nom: string;
  url: string;
  logo: string;
};

export type FranceTravailLieuTravail = {
  libelle: string;
  latitude: number;
  longitude: number;
  codePostal: string;
  commune: string;
};

export type FranceTravailEntreprise = {
  nom: string;
  description: string;
  logo: string;
  url: string;
  entrepriseAdaptee: boolean;
};

export type FranceTravailSalaire = {
  libelle: string;
  commentaire: string;
  complement1: string;
  complement2: string;
  listeComplements: string;
};

export type FranceTravailContact = {
  nom: string;
  coordonnees1: string;
  coordonnees2: string;
  coordonnees3: string;
  telephone: string;
  courriel: string;
  commentaire: string;
  urlRecruteur: string;
  urlPostulation: string;
};

export type FranceTravailAgence = {
  telephone: string;
  courriel: string;
};

export type FranceTravailOrigineOffre = {
  origine: string;
  urlOrigine: string;
  partenaires: FranceTravailPartner[];
};

export type FranceTravailContexteTravail = {
  horaires: string;
  conditionsExercice: string;
};

export type FranceTravailJob = {
  id: string;
  intitule: string;
  description: string;
  dateCreation: string;
  dateActualisation: string;
  lieuTravail: FranceTravailLieuTravail;
  romeCode: string;
  romeLibelle: string;
  appellationlibelle: string;
  entreprise: FranceTravailEntreprise;
  typeContrat: string;
  typeContratLibelle: string;
  natureContrat: string;
  experienceExige: string;
  experienceLibelle: string;
  experienceCommentaire: string;
  formations: FranceTravailFormation[];
  langues: FranceTravailLanguage[];
  permis: FranceTravailPermit[];
  outilsBureautiques: string;
  competences: FranceTravailCompetence[];
  salaire: FranceTravailSalaire;
  dureeTravailLibelle: string;
  dureeTravailLibelleConverti: string;
  complementExercice: string;
  conditionExercice: string;
  alternance: boolean;
  contact: FranceTravailContact;
  agence: FranceTravailAgence;
  nombrePostes: number;
  accessibleTH: boolean;
  deplacementCode: string;
  deplacementLibelle: string;
  qualificationCode: string;
  qualificationLibelle: string;
  codeNAF: string;
  secteurActivite: string;
  secteurActiviteLibelle: string;
  qualitesProfessionnelles: FranceTravailProfessionalQuality[];
  trancheEffectifEtab: string;
  origineOffre: FranceTravailOrigineOffre;
  offresManqueCandidats: boolean;
  contexteTravail: FranceTravailContexteTravail;
  entrepriseAdaptee: boolean;
  employeurHandiEngage: boolean;
};

export type FranceTravailFiltreAggregation = {
  valeurPossible: string;
  nbResultats: number;
};

export type FranceTravailFiltrePossible = {
  filtre: string;
  agregation: FranceTravailFiltreAggregation[];
};

export type FranceTravailJobsResponse = {
  resultats: FranceTravailJob[];
  filtresPossibles: FranceTravailFiltrePossible[];
};

export const FRANCE_TRAVAIL_CONTRACT_TYPE_CODES = {
  FIXED_TERM: 'CDD',
  PERMANENT: 'CDI',
  TEMPORARY: 'MIS',
  SEASONAL: 'SAI',
} as const;

export const FRANCE_TRAVAIL_CONTRACT_TYPES_LABELS = {
  [FRANCE_TRAVAIL_CONTRACT_TYPE_CODES.FIXED_TERM]: 'Contrat à durée déterminée',
  [FRANCE_TRAVAIL_CONTRACT_TYPE_CODES.PERMANENT]: 'Contrat à durée indéterminée',
  [FRANCE_TRAVAIL_CONTRACT_TYPE_CODES.TEMPORARY]: 'Mission intérimaire',
  [FRANCE_TRAVAIL_CONTRACT_TYPE_CODES.SEASONAL]: 'Contrat travail saisonnier',
} as const;

// Apprenticeship has no `typeContrat` code — the FT offres API filters it via `natureContrat`.
export const FRANCE_TRAVAIL_NATURE_CONTRACT_CODES = {
  APPRENTICESHIP: 'E2',
} as const;

export type FranceTravailProfessionalDomain = {
  code: string;
  label: string;
};

// Codes from the FT offres API "grands domaines" referential, usable as the
// `grandDomaine` search param. Ordered alphabetically by label for display.
export const FRANCE_TRAVAIL_PROFESSIONAL_DOMAINS: readonly FranceTravailProfessionalDomain[] = [
  { code: 'M', label: 'Achats / Comptabilité / Gestion' },
  { code: 'A', label: 'Agriculture / Pêche / Espaces verts et naturels / Soins aux animaux' },
  { code: 'B', label: "Arts / Artisanat d'art" },
  { code: 'C', label: 'Banque / Assurance' },
  { code: 'F', label: 'Bâtiment / Travaux Publics' },
  { code: 'D', label: 'Commerce / Vente' },
  { code: 'E', label: 'Communication / Multimédia' },
  { code: 'M14', label: 'Conseil / Etudes' },
  { code: 'M13', label: "Direction d'entreprise" },
  { code: 'G', label: 'Hôtellerie - Restauration / Tourisme / Animation' },
  { code: 'C15', label: 'Immobilier' },
  { code: 'H', label: 'Industrie' },
  { code: 'M18', label: 'Informatique / Télécommunication' },
  { code: 'I', label: 'Installation / Maintenance' },
  { code: 'M17', label: 'Marketing / Stratégie commerciale' },
  { code: 'M15', label: 'Ressources Humaines' },
  { code: 'J', label: 'Santé' },
  { code: 'M16', label: 'Secrétariat / Assistanat' },
  { code: 'K', label: 'Services à la personne / à la collectivité' },
  { code: 'L', label: 'Spectacle' },
  { code: 'L14', label: 'Sport' },
  { code: 'N', label: 'Transport / Logistique' },
] as const;

export enum FranceTravailExperienceLevel {
  NOT_SPECIFIED = '0',
  LESS_THAN_ONE_YEAR = '1',
  ONE_TO_THREE_YEARS = '2',
  MORE_THAN_THREE_YEARS = '3',
}

export const FRANCE_TRAVAIL_EXPERIENCE_LEVEL_OPTIONS: readonly {
  value: FranceTravailExperienceLevel;
  label: string;
}[] = [
  { value: FranceTravailExperienceLevel.LESS_THAN_ONE_YEAR, label: 'Moins de 1 an' },
  { value: FranceTravailExperienceLevel.ONE_TO_THREE_YEARS, label: 'De 1 à 3 ans' },
  { value: FranceTravailExperienceLevel.MORE_THAN_THREE_YEARS, label: 'Plus de 3 ans' },
  { value: FranceTravailExperienceLevel.NOT_SPECIFIED, label: 'Non renseignée' },
] as const;

export enum FranceTravailSalaryPeriod {
  MONTHLY = 'M',
  ANNUAL = 'A',
  HOURLY = 'H',
  FEE = 'C',
}

export const FRANCE_TRAVAIL_SALARY_PERIOD_OPTIONS: readonly {
  value: FranceTravailSalaryPeriod;
  label: string;
}[] = [
  { value: FranceTravailSalaryPeriod.MONTHLY, label: 'Mensuel' },
  { value: FranceTravailSalaryPeriod.ANNUAL, label: 'Annuel' },
  { value: FranceTravailSalaryPeriod.HOURLY, label: 'Horaire' },
  { value: FranceTravailSalaryPeriod.FEE, label: 'Cachet' },
] as const;
